<template>
    <div class="d-flex flex-column">
        <div class="upload-header d-flex mb-2">
            <h4 class="d-inline me-auto">{{ title }}</h4>
            <div
                class="upload-document"
                v-if="fileDetails.length <= 0 && addPatientDocumentsPermission && !isHistoryPage"
            >
                <button type="button" class="btn btn-primary border border-primary">Add Documents</button>
                <input type="file" @change="uploadFilesChanged($event)" class="file-input" multiple />
            </div>
        </div>
        <div class="upload-filters">
            <slot name="document-filters"></slot>
        </div>
        <div class="upload-body">
            <div class="new-documents" v-if="fileDetails.length > 0">
                <div v-if="!uploading">
                    <table class="table table-responsive-md borderless">
                        <thead>
                            <tr class="bg-table-header">
                                <th></th>
                                <th>Name</th>
                                <th>Type <span class="text-danger">*</span></th>
                                <th class="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody v-for="(fileDetail, index) in fileDetails">
                            <AddDocumentRow
                                :file-detail="fileDetail"
                                :get-file-icon="getFileIcon"
                                :uploading="uploading"
                                :searched-types="searchedTypes"
                                :file-index="index"
                                :is-field-required="isFieldRequired"
                                v-on:remove-file="removeFile(index)"
                            />
                        </tbody>
                    </table>
                    <footer class="upload-footer pb-2 d-flex align-items-center justify-content-end">
                        <span class="more-files-wrapper me-2">
                            <a href="javascript:void(0);">Add Another Document</a>
                            <input
                                type="file"
                                class="file-input"
                                multiple
                                @change="moreFilesAdded($event)"
                                :disabled="uploading"
                            />
                        </span>
                        <button
                            type="button"
                            class="btn btn-xs btn-primary me-2"
                            @click="uploadDocuments()"
                            :disabled="uploading"
                        >
                            Save Documents
                        </button>
                        <button
                            type="button"
                            class="btn btn-xs btn-outline-primary"
                            @click="removeAllFiles()"
                            :disabled="uploading"
                        >
                            Cancel
                        </button>
                    </footer>
                </div>
                <div class="mb-4" v-else>
                    <h5><i class="fa fa-spinner fa-spin"></i> Uploading</h5>
                </div>
            </div>
            <table class="table table-responsive-md borderless">
                <thead>
                    <tr class="bg-table-header">
                        <th>Name</th>
                        <th>Type</th>
                        <th>Last Modified</th>
                        <th class="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody v-if="!patientDocuments.length && !loadingDocuments">
                    <TableEmpty :col-span="5" component="documents" secondary-message="No Documents Found" />
                </tbody>
                <tbody v-else>
                    <tr v-if="loadingDocuments" v-for="i in loadingCount">
                        <td><span class="shimmer-loading block-size-8"></span></td>
                        <td><span class="shimmer-loading block-size-6"></span></td>
                        <td><span class="shimmer-loading block-size-6"></span></td>
                        <td><span class="shimmer-loading block-size-4"></span></td>
                    </tr>
                    <tr v-if="!loadingDocuments" v-for="document in patientDocuments as any">
                        <td>
                            <i :class="`file-icon far fa-file ${getFileIcon(document.mimeType)} me-2`"></i>
                            <a :href="document.url" target="_blank">{{ document.documentName }}</a>
                        </td>
                        <td>{{ document.uploadType }}</td>
                        <td>{{ formatDate(document.modifiedOn) }}</td>
                        <td class="text-end" v-if="!isHistoryPage">
                            <template v-if="deletePatientDocumentsPermission">
                                <button
                                    type="button"
                                    class="btn btn-xs btn-outline-danger icon-del"
                                    @click="deleteSelectedDocument(document.id)"
                                    v-if="!document.hasOwnProperty('IsPatientChart')"
                                >
                                 <span><i class="fas fa-trash-alt"></i> Delete</span>   
                                </button>
                                <button
                                    type="button"
                                    class="btn table-btn btn-outline-info au-target"
                                    @click="openSelectedDocument(document)"
                                    v-tooltip="{ title: 'View' }"
                                    v-if="document.isPatientChart && document.Status != 4 && document.Status != 'Running'"
                                >
                                    <i class="fa fa-eye"></i>
                                </button>
                            </template>
                            <i class="text-default" v-else>No Actions</i>
                        </td>
                        <td v-if="isHistoryPage && isLoadingComment">
                            <span class="shimmer-loading block-size-4"></span>
                        </td>
                        <td class="d-flex justify-content-end" v-if="isHistoryPage && !isLoadingComment">
                            <div class="btn-group table-btn-group my-auto">
                                <button
                                    type="button"
                                    class="btn px-1 py-0 btn-outline-primary dropdown-toggle"
                                    data-bs-toggle="dropdown"
                                    :id="`document__Actions${document.id}`"
                                >
                                    <i class="fas fa-ellipsis-h"></i>
                                </button>
                                <div class="dropdown-menu" :ria-labelledby="`document__Action${document.id}`">
                                    <a
                                        class="dropdown-item text-danger"
                                        href="javascript:void(0);"
                                        @click="deleteSelectedDocument(document.id)"
                                        v-if="deletePatientDocumentsPermission"
                                    >
                                        Delete
                                    </a>
                                    <a class="dropdown-item" href="javascript:void(0);" @click="createComment()">
                                        Comment
                                    </a>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script lang="ts" src="./PatientDocuments.ts" />
<style lang="scss" src="./PatientDocuments.scss"></style>
